import { debounce } from "lodash";
import { useCallback } from "react";
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useBeforeUnload } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import Layout from "../../components/Layout";
import Modal from "../../components/Modal";
import {
  faTimes,
  faCirclePlus,
  faCircleExclamation,
  faCircleCheck,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import ReactCrop, { Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";
import ErrorModal from "../../components/ErrorModal";

// 型定義
type User = {
  id: string;
  last_name: string;
  first_name: string;
  last_name_kana: string;
  first_name_kana: string;
  first_name_en: string;
  last_name_en: string;
  email: string;
  department_id: string;
  employee_id: string;
  it_company_join_date: string;
  join_date: string;
  gender: string;
  birth_date: string;
  nationality: string;
  postal_code: string;
  current_address: string;
  image_url: string;
  image_path: string;
  qualifications?: {
    id: string;
    name: string;
    pivot: {
      qualification_id: number;
      qualification_get_date: string;
    };
  }[];
  is_saved_profile?: boolean;
  is_admin?: boolean;
  password?: string;
};

type Qualification = {
  id: string | null;
  qualification_id: string | null;
  get_date: string | null;
  rowKey?: string; // UI用の一意キー（API送信時は除外）
};

type QualificationOption = {
  id: string;
  name: string;
};

type ApiResponse = {
  user: User;
  qualifications: {
    id: string;
    name: string;
    pivot: {
      user_id: number;
      qualification_id: number;
      qualification_get_date: string;
    };
  }[];
  qualificationOptions: QualificationOption[];
  departmentOptions: { id: number; name: string }[];
};

type OriginalImage = {
  originalWidth: number;
  originalHeight: number;
  isPortrait: boolean;
};

type DisplayImage = {
  displayWidth: number;
  displayHeight: number;
  ratio: number;
};

const MyPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [qualifications, setQualifications] = useState<Qualification[]>([]);
  const [qualificationOptions, setQualificationOptions] = useState<
    QualificationOption[]
  >([]);
  const [departmentOptions, setDepartmentOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(true); //全体のローディング状態
  const [isSaving, setIsSaving] = useState(false); //保存中の状態
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    "入力内容に誤りがあるか、予期せぬエラーが発生しました。"
  );
  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState<string>("");

  // ログインユーザーの情報も取得
  const { authUser, fetchUser } = useUser();

  const [isNew, setIsNew] = useState<boolean>(!id);
  const [password, setPassword] = useState<string>(isNew ? "isopra000" : "");

  //userの情報をUserContextから取得
  let isAdmin = authUser?.isPublicAdmin && authUser.isPublicAdmin;
  let isSystemAdmin = authUser?.isSystemAdmin && authUser.isSystemAdmin;

  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [originalImage, setOriginalImage] = useState<OriginalImage | null>(
    null
  ); // 実際の画像サイズと縦長かどうか
  const [displayImage, setDisplayImage] = useState<DisplayImage | null>(null); // ブラウザ上の表示サイズ
  const [crop, setCrop] = useState<Crop>({
    unit: "px",
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // ページ全体を監視
  useEffect(() => {
    // 入力内容の変更時
    const handleChange = (e: Event) => {
      const target = e.target as HTMLElement;
      const tag = target.tagName;
      // 特定の入力系要素は対象
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) {
        setIsDirty((prev) => (prev ? prev : true));
      }
    };

    // ボタンのクリック時
    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      // 特定クラス（ignore-dirtyとmodal）のボタンは対象外
      if (target.closest(".ignore-dirty")) return;
      if (target.closest(".modal")) return;

      // 上記以外のボタンクリックはすべて対象
      if (target.closest("button")) {
        setIsDirty((prev) => (prev ? prev : true));
      }
    };

    document.addEventListener("input", handleChange);
    document.addEventListener("change", handleChange);
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("input", handleChange);
      document.removeEventListener("change", handleChange);
      document.removeEventListener("click", handleClick);
    };
  }, []);

  // ブラウザを閉じる / タブを閉じる / ページをリロードするときに警告を出す
  useBeforeUnload((event: BeforeUnloadEvent) => {
    if (isDirty) {
      event.preventDefault();
      event.returnValue = "";
    }
  });

  // アプリ内で他ページに遷移するときに警告を出す
  const handleNavigate = (path: string) => {
    if (
      isDirty &&
      !window.confirm("行った変更が保存されない可能性があります。")
    )
      return;
    navigate(path);
  };

  // 初回ロード時の処理
  useEffect(() => {
    if (id) {
      fetchUserData();
      setIsNew(false); // 編集モード
    } else {
      fetchMasterData();
      // 新規登録時の初期値
      setUser({
        id: "",
        last_name: "",
        first_name: "",
        last_name_kana: "",
        first_name_kana: "",
        first_name_en: "",
        last_name_en: "",
        email: "",
        department_id: "",
        employee_id: "",
        it_company_join_date: "",
        join_date: "",
        gender: "",
        birth_date: "",
        nationality: "",
        postal_code: "",
        current_address: "",
        image_url: "",
        image_path: "",
        qualifications: [],
        is_saved_profile: false,
      });
      setIsNew(true); // 新規作成モード
      setPassword("isopra000");
    }
  }, [id]);

  // APIからユーザー情報を取得
  const fetchUserData = async () => {
    try {
      const response = await axios.get<ApiResponse>(
        `${process.env.REACT_APP_BACKEND_URL}/api/main/user-profile/get-target-user/${id}`,
        { withCredentials: true }
      );

      const { user, qualificationOptions, departmentOptions } = response.data;

      // ユーザー情報のセット
      setUser(user);

      // 資格マスタリストをセット
      setQualificationOptions(qualificationOptions);

      // 部署オプションをセット
      setDepartmentOptions(departmentOptions);

      // ユーザーの資格情報を分離してセット（資格情報がある場合のみ）
      const formattedQualifications =
        user.qualifications?.map((q) => ({
          id: q.id,
          qualification_id: q.pivot.qualification_id.toString(),
          get_date: q.pivot.qualification_get_date,
          rowKey: crypto.randomUUID(), // UI用の一意キー
        })) || [];

      setQualifications(formattedQualifications);
    } catch (error) {
      console.error("ユーザー情報取得エラー:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // マスタデータのみ取得
  const fetchMasterData = async () => {
    try {
      const response = await axios.get<ApiResponse>(
        `${process.env.REACT_APP_BACKEND_URL}/api/main/user-profile/get-master-data`,
        { withCredentials: true }
      );

      const { qualificationOptions, departmentOptions } = response.data;
      setQualificationOptions(qualificationOptions);
      setDepartmentOptions(departmentOptions);
    } catch (error) {
      console.error("マスタデータ取得エラー:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 画像のロード状態を更新
  const handleImageLoad = () => {
    setIsImageLoaded(true);
  };

  // ユーザー情報更新
  const handleInputChange = (field: keyof User, value: any) => {
    setUser((prevUser) => {
      return prevUser ? { ...prevUser, [field]: value } : null;
    });
  };

  // 資格情報変更
  const handleChangeQualification = (
    idx: number,
    field: "qualification_id" | "get_date",
    value: string
  ) => {
    const updatedQualifications = qualifications.map((qualification, index) =>
      index === idx
        ? {
            ...qualification,
            [field]: field === "get_date" ? `${value}-01` : value, // 月に対して1日を追加
          }
        : qualification
    );
    setQualifications(updatedQualifications);
  };

  // 資格追加
  const handleQualificationAdd = () => {
    setQualifications([
      ...qualifications,
      {
        id: null,
        qualification_id: null,
        get_date: null,
        rowKey: crypto.randomUUID(), // UI用の一意キー
      },
    ]);
  };

  // 資格削除（rowKeyで特定して削除）
  const handleQualificationDelete = (rowKey: string) => {
    setQualifications((prev) => prev.filter((q) => q.rowKey !== rowKey));
  };

  // 資格情報バリデーション
  const validateQualifications = () => {
    for (const qualification of qualifications) {
      if (!qualification.qualification_id || !qualification.get_date) {
        return "資格IDと取得日は必須です。";
      }
    }
    return "";
  };

  // 保存処理
  const handleSubmit = async () => {
    if (!user) {
      setErrorModalOpen(true);
      return;
    }

    if (isNew || user.is_saved_profile || isAdmin) {
      setConfirmText("変更内容を保存します。");
    } else {
      setConfirmText(
        "現住所と資格情報以外は1度しか登録できません。よろしいですか？"
      );
    }
    setIsConfirmModalOpen(true);
    setIsDirty(false); // 未保存フラグをリセット
  };

  const handleConfirmSubmit = async () => {
    setIsConfirmModalOpen(false);
    setErrors({});
    try {
      setIsSaving(true);
      const url = isNew
        ? `${process.env.REACT_APP_BACKEND_URL}/api/main/user-profile/create-user`
        : `${process.env.REACT_APP_BACKEND_URL}/api/main/user-profile/update-target-user/${id}`;

      // API送信前にrowKeyを除外
      const qualificationsForApi = qualifications.map(
        ({ rowKey, ...rest }) => rest
      );
      const payload: any = { user, qualifications: qualificationsForApi };
      if (isNew && password) payload.user.password = password;

      await axios.post(url, payload, { withCredentials: true });
      setIsSaving(false);
      setSuccessModalOpen(true);
      fetchUser();
      if (isNew) navigate("/main/admin");
      else fetchUserData();
    } catch (error: any) {
      setIsSaving(false);
      if (error.response?.status === 422) {
        setErrors(error.response.data.errors);
        setErrorMessage("入力内容に誤りがあります。各項目を確認してください。");
      } else {
        setErrorMessage("予期せぬエラーが発生しました。");
      }
      setErrorModalOpen(true);
    }
  };

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const openImageModal = () => {
    setPreviewUrl("");
    setIsImageModalOpen(true);
  };

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);

  // トリミング領域の初期値設定
  const setInitialCropValues = (
    imgElement: HTMLImageElement,
    image: HTMLImageElement,
    initialRatio: number
  ) => {
    const { width: originalWidth, height: originalHeight } = image; // 実際の画像サイズ
    const isPortrait = originalHeight > originalWidth; // 縦長かどうか
    setOriginalImage({ originalWidth, originalHeight, isPortrait });
    const { width: displayWidth, height: displayHeight } =
      imgElement.getBoundingClientRect(); // ブラウザ上の表示サイズ
    const ratio = isPortrait
      ? originalHeight / displayHeight
      : originalWidth / displayWidth;
    setDisplayImage({ displayWidth, displayHeight, ratio });

    const size =
      (Math.min(originalWidth, originalHeight) / ratio) * initialRatio; // 縦横で短い方の長さ * ratio
    const x = (displayWidth - size) / 2;
    const y = (displayHeight - size) / 2;

    setCrop({
      unit: "px",
      x: x,
      y: y,
      width: size,
      height: size,
    });
  };

  // 切り取り領域範囲を制限
  const clampCrop = (c: Crop) => {
    if (!originalImage || !displayImage) {
      return { ...c, x: 0, y: 0, width: 0, height: 0 };
    }

    const minX = originalImage.isPortrait
      ? (originalImage.originalHeight - originalImage.originalWidth) /
        displayImage.ratio /
        2
      : 0;
    const minY = originalImage.isPortrait
      ? 0
      : (originalImage.originalWidth - originalImage.originalHeight) /
        displayImage.ratio /
        2;
    const maxX = displayImage.displayWidth - minX;
    const maxY = displayImage.displayHeight - minY;

    const minWidth = 50;
    const minHeight = 50;

    // 最大幅と最大高さを計算する
    const maxWidth = originalImage.isPortrait
      ? maxX - Math.max(c.x, minX)
      : displayImage.displayWidth - c.x;
    const maxHeight = originalImage.isPortrait
      ? displayImage.displayHeight - c.y
      : maxY - Math.max(c.y, minY);

    const width = Math.max(minWidth, Math.min(c.width, maxWidth));
    const height = Math.max(minHeight, Math.min(c.height, maxHeight));

    const adjustedLength = Math.min(width, height); // アスペクト比を1:1に調整

    // x, y が範囲内に収まるように調整
    const adjustedX = Math.max(minX, Math.min(c.x, maxX - adjustedLength));
    const adjustedY = Math.max(minY, Math.min(c.y, maxY - adjustedLength));

    return {
      ...c,
      x: adjustedX,
      y: adjustedY,
      width: adjustedLength,
      height: adjustedLength,
    };
  };

  // 画像ファイルのバリデーション
  const validateImageFile = (file: File, limit: number) => {
    // ファイルの拡張子を制限
    const allowedExtensions = ["jpg", "jpeg", "png"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      setErrorMessage(
        "許可されていないファイル形式です。jpg, jpeg, png の画像を選択してください。"
      );
      setErrorModalOpen(true);
      return false;
    }

    // 画像形式のチェック
    if (!file.type.startsWith("image/")) {
      setErrorMessage("選択されたファイルは画像ではありません。");
      setErrorModalOpen(true);
      return false;
    }

    // サイズ制限（1MB）
    const maxSize = limit * limit;
    if (file.size > maxSize) {
      setErrorMessage(
        "画像のサイズが大きすぎます。1MB以内の画像を選択してください。"
      );
      setErrorModalOpen(true);
      return false;
    }

    return true;
  };

  // プロフィール画像変更
  const handleChangeImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!validateImageFile(file, 1024)) {
      event.target.value = ""; // 不適切なファイルの場合は選択をリセット
      return;
    }

    setSelectedImage(file);
    setPreviewUrl(URL.createObjectURL(file));

    const image = new Image();
    image.src = URL.createObjectURL(file);
    image.onload = () => {
      const imgElement = document.querySelector(
        "img#preview"
      ) as HTMLImageElement;
      if (!imgElement) return;
      setInitialCropValues(imgElement, image, 0.75);
    };
  };

  // ドラッグオーバー時
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  // ドラッグリーブ時
  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // ドロップ時に画像をセット
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files[0];

    if (!validateImageFile(file, 1024)) {
      return;
    }

    setSelectedImage(file);
    setPreviewUrl(URL.createObjectURL(file));

    const image = new Image();
    image.src = URL.createObjectURL(file);
    image.onload = () => {
      const imgElement = document.querySelector(
        "img#preview"
      ) as HTMLImageElement;
      if (!imgElement) return;
      setInitialCropValues(imgElement, image, 0.75);
    };
  };

  const handleDeletePreview = () => {
    setPreviewUrl("");
  };

  const cropImage = (
    image: HTMLImageElement,
    cropData: Crop,
    extension: string
  ) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setErrorMessage("Canvas contextの取得に失敗しました。");
      setErrorModalOpen(true);
      return null;
    }

    if (!originalImage || !displayImage) return;

    const length = cropData.width * displayImage.ratio;

    const x = originalImage.isPortrait
      ? cropData.x * displayImage.ratio -
        (originalImage.originalHeight - originalImage.originalWidth) / 2
      : cropData.x * displayImage.ratio;

    const y = originalImage.isPortrait
      ? cropData.y * displayImage.ratio
      : cropData.y * displayImage.ratio -
        (originalImage.originalWidth - originalImage.originalHeight) / 2;

    canvas.width = canvas.height = length;
    ctx.drawImage(image, x, y, length, length, 0, 0, length, length); // トリミングした画像を描画

    return canvas.toDataURL(`image/${extension}`); // トリミング画像を指定した拡張子形式で取得
  };

  const handleSaveImage = async () => {
    if (!selectedImage) {
      setErrorModalOpen(true);
      return;
    }

    const cropData = crop;
    const image = new Image();
    image.src = previewUrl;

    if (cropData.width === 0) {
      setErrorModalOpen(true);
      return;
    }

    image.onload = async () => {
      if (!displayImage) return;
      const croppedImageData = cropImage(image, cropData, "jpeg");
      if (!croppedImageData) return;

      const byteString = atob(croppedImageData.split(",")[1]);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      const fileName = `${user?.employee_id}.png`;
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([uint8Array], { type: "image/jpeg" });
      const file = new File([blob], `${user?.employee_id}.jpeg`, {
        type: "image/jpeg",
      });

      const oldFile = user?.image_path;

      const formData = new FormData();
      formData.append("user_id", user?.id?.toString() || "");
      formData.append("profile_image", file);
      formData.append("file_name", fileName);
      if (oldFile && oldFile !== "0") {
        formData.append("old_file", oldFile);
      }

      try {
        setIsSaving(true);
        const response = await axios.post<{
          signed_url: string;
          image_path: string;
        }>(
          `${process.env.REACT_APP_BACKEND_URL}/api/main/user-profile/change-profile-image`,
          formData,
          {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        fetchUser();
        setIsImageModalOpen(false);
        setSuccessModalOpen(true);

        const { signed_url, image_path } = response.data;
        if (signed_url && image_path) {
          setUser((prev) =>
            prev ? { ...prev, image_url: signed_url, image_path } : prev
          );
          setIsImageModalOpen(false);
          setSuccessModalOpen(true);
          setIsDirty(false); // 未保存フラグをリセット
        } else {
          setErrorModalOpen(true);
        }
      } catch (error: any) {
        console.error("アップロードエラー:", error);
        if (error.response?.status === 422 && error.response?.data?.errors) {
          const errors = error.response.data.errors;
          const firstKey = Object.keys(errors)[0];
          const firstMessage = errors[firstKey][0];
          setErrorMessage(firstMessage); // カスタムエラー表示
        } else {
          setErrorMessage(
            "画像のアップロードに失敗しました。もう一度お試しください。"
          );
        }
        setErrorModalOpen(true);
      } finally {
        setIsSaving(false);
      }
    };
  };

  // パスワード変更
  const handleChangePassword = () => {
    navigate("/main/change-password");
  };

  // 郵便番号から住所検索
  const fetchAddressByPostalCode = useCallback(
    debounce(async (zip: string) => {
      try {
        const response = await axios.get(
          `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zip}`
        );

        const result = response.data as {
          results:
            | { address1: string; address2: string; address3: string }[]
            | null;
          message: string;
        };

        if (result.results && result.results.length > 0) {
          const data = result.results[0];
          handleInputChange(
            "current_address",
            `${data.address1}${data.address2}${data.address3}`
          );
        } else {
          setErrorMessage("該当する住所が見つかりませんでした。");
        }
      } catch (error) {
        console.error("住所取得エラー:", error);
        setErrorMessage("住所の取得に失敗しました。");
      }
    }, 300), // 300msのデバウンス
    []
  );

  // レンダリング
  return (
    <Layout>
      <div className="flex items-center justify-center">
        <div className="p-10 bg-white rounded-3xl">
          <div className="flex mb-4">
            <div className="title-text font-black">プロフィール</div>
          </div>
          <div className="flex mb-6">
            {isLoading || isSaving ? (
              // スケルトンスクリーン
              <>
                <div className="space-y-4">
                  {/* Profile Image Skeleton */}
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 rounded-full bg-slate-200 mb-4 animate-pulse"></div>
                  </div>

                  {/* Buttons Skeleton */}
                  <div className="flex space-x-4 justify-center">
                    <div className="w-36 h-8 bg-slate-200 rounded-3xl animate-pulse"></div>
                    <div className="w-36 h-8 bg-slate-200 rounded-3xl animate-pulse"></div>
                  </div>

                  {/* Profile Table Skeleton */}
                  <div className="w-full border-2 border-slate-300 rounded-2xl overflow-hidden mb-8">
                    <table className="table-auto text-slate-600 w-full border-collapse">
                      <tbody>
                        {[...Array(9)].map((_, idx) => (
                          <tr key={idx}>
                            <th className="w-40 bg-slate-50 px-4 py-2 text-right border-r-2">
                              <div className="w-full h-6 bg-slate-200 rounded w-24 animate-pulse"></div>
                            </th>
                            <th className="w-48 bg-white px-4 py-2">
                              <div className="h-6 w-full bg-slate-200 rounded w-24 animate-pulse"></div>
                            </th>
                            <th className="w-48 bg-white px-4 py-2">
                              <div className="h-6 w-full bg-slate-200 rounded w-24 animate-pulse"></div>
                            </th>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Qualifications Skeleton */}
                  <div className="w-full border-2 border-slate-300 rounded-2xl overflow-hidden">
                    <table className="table-auto text-slate-600 w-full border-collapse">
                      <thead className="border-b-2">
                        <tr>
                          <th className="bg-slate-100 px-4 py-2 border-r-2">
                            <div className="h-6 bg-slate-200 rounded w-12 animate-pulse"></div>
                          </th>
                          <th className="bg-slate-100 px-4 py-2 border-r-2">
                            <div className="h-6 bg-slate-200 rounded w-36 animate-pulse"></div>
                          </th>
                          <th className="bg-slate-100 px-4 py-2 border-r-2">
                            <div className="h-6 bg-slate-200 rounded w-24 animate-pulse"></div>
                          </th>
                          <th className="bg-slate-100 px-4 py-2">
                            <div className="h-6 bg-slate-200 rounded w-12 animate-pulse"></div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...Array(3)].map((_, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 text-center border-r-2 bg-slate-50">
                              <div className="h-6 bg-slate-200 rounded w-12 animate-pulse"></div>
                            </td>
                            <td className="px-4 py-2 border-r-2 bg-white">
                              <div className="h-6 bg-slate-200 rounded w-36 animate-pulse"></div>
                            </td>
                            <td className="px-4 py-2 border-r-2 bg-white">
                              <div className="h-6 bg-slate-200 rounded w-24 animate-pulse"></div>
                            </td>
                            <td className="px-4 py-2 bg-white">
                              <div className="h-6 bg-slate-200 rounded w-12 animate-pulse"></div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col items-center">
                  <>
                    <img
                      src={`${user?.image_url}`}
                      alt="Profile"
                      className={`w-24 h-24 object-cover mb-4 rounded-full ${
                        isImageLoaded ? "" : "hidden"
                      }`}
                      onLoad={handleImageLoad}
                    />
                    {!isImageLoaded && (
                      <div className="w-24 h-24 bg-slate-200 animate-pulse mb-4 rounded-full"></div>
                    )}
                  </>
                </div>
                <div className="flex space-x-4 justify-center">
                  {authUser?.id === user?.id ? (
                    <>
                      <button
                        onClick={openImageModal}
                        className="ignore-dirty bg-gray-700 text-white py-1 px-4 rounded-3xl hover:bg-opacity-50"
                      >
                        プロフィール画像変更
                      </button>
                      <button
                        onClick={handleChangePassword}
                        className="ignore-dirty bg-gray-700 text-white py-1 px-4 rounded-3xl hover:bg-opacity-50"
                      >
                        パスワード変更
                      </button>
                    </>
                  ) : (
                    <></>
                  )}
                </div>
                <form onSubmit={handleSubmit}>
                  {/* 変更ボタン */}
                  <div className="flex mb-4">
                    <div className="rounded-full mt-1.5 border-slate-100 mr-3 w-5 h-5 title-circle"></div>
                    <div className="text-lg text-slate-500 font-black">
                      ユーザー情報
                    </div>
                  </div>
                  {/* プロフィールテーブル */}
                  {/* ログインユーザーと画面のURL末尾のUser情報のユーザidが一致しているときはis_saved_plofileがtrueのときは住所と資格以外変更不可 */}
                  <div className="w-full border-2 border-slate-300 rounded-2xl overflow-hidden mb-8">
                    <table className="table-auto text-slate-600 w-full border-collapse">
                      <tbody
                        className="[&>tr:nth-child(odd)>th]:bg-slate-100
                                        [&>tr:nth-child(odd)>td]:bg-white
                                        [&>tr:nth-child(even)>th]:bg-[#eaf0f6]
                                        [&>tr:nth-child(even)>td]:bg-slate-50"
                      >
                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            氏名
                            {isNew && (
                              <span className="ml-2 inline-block bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded">
                                必須
                              </span>
                            )}
                          </th>
                          <td className="px-4 py-2">
                            <div className="flex space-x-4">
                              <input
                                type="text"
                                value={user?.last_name || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange("last_name", e.target.value)
                                }
                                placeholder="姓"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                              <input
                                type="text"
                                value={user?.first_name || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "first_name",
                                    e.target.value
                                  )
                                }
                                placeholder="名"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                            </div>
                            {/* エラーメッセージ表示 */}
                            {errors["user.last_name"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.last_name"][0]}
                              </p>
                            )}
                            {errors["user.first_name"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.first_name"][0]}
                              </p>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            氏名（カナ）
                          </th>
                          <td className="px-4 py-2">
                            <div className="flex space-x-4">
                              <input
                                type="text"
                                value={user?.last_name_kana || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "last_name_kana",
                                    e.target.value
                                  )
                                }
                                placeholder="セイ"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                              <input
                                type="text"
                                value={user?.first_name_kana || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "first_name_kana",
                                    e.target.value
                                  )
                                }
                                placeholder="メイ"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                            </div>
                            {/* エラーメッセージ表示 */}
                            {errors["user.last_name_kana"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.last_name_kana"][0]}
                              </p>
                            )}
                            {errors["user.first_name_kana"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.first_name_kana"][0]}
                              </p>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            氏名（英）
                          </th>
                          <td className="px-4 py-2">
                            <div className="flex space-x-4">
                              <input
                                type="text"
                                value={user?.last_name_en || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "last_name_en",
                                    e.target.value
                                  )
                                }
                                placeholder="Sei"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                              <input
                                type="text"
                                value={user?.first_name_en || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "first_name_en",
                                    e.target.value
                                  )
                                }
                                placeholder="Mei"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                            </div>
                            {/* エラーメッセージ表示 */}
                            {errors["user.last_name_en"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.last_name_en"][0]}
                              </p>
                            )}
                            {errors["user.first_name_en"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.first_name_en"][0]}
                              </p>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            メールアドレス
                            {isNew && (
                              <span className="ml-2 inline-block bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded">
                                必須
                              </span>
                            )}
                          </th>
                          <td className="px-4 py-2">
                            <div className="flex">
                              <input
                                type="email"
                                value={user?.email || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange("email", e.target.value)
                                }
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                            </div>
                            {/* エラーメッセージ表示 */}
                            {errors["user.email"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.email"][0]}
                              </p>
                            )}
                          </td>
                        </tr>
                        {isNew && (
                          <tr>
                            <th className="w-40 px-4 py-2 text-right border-r-2">
                              初期パスワード
                              <span className="ml-2 inline-block bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded">
                                必須
                              </span>
                            </th>
                            <td className="px-4 py-2">
                              <div className="flex">
                                <input
                                  value={password}
                                  onChange={(e) => setPassword(e.target.value)}
                                  placeholder="初回ログイン用の仮パスワードを入力"
                                  className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-blue-300"
                                />
                              </div>
                              {/* エラー表示 */}
                              {errors["user.password"] && (
                                <p className="text-red-500 text-sm mt-1">
                                  <FontAwesomeIcon
                                    icon={faCircleExclamation}
                                    className="mr-1"
                                  />
                                  {errors["user.password"][0]}
                                </p>
                              )}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            所属
                          </th>
                          <td className="px-4 py-2">
                            <div>
                              <select
                                defaultValue={user?.department_id || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "department_id",
                                    parseInt(e.target.value)
                                  )
                                }
                                className="border border-slate-300 px-2 py-2 rounded-lg focus:outline-none focus:ring focus:border-blue-300 w-full disabled:bg-gray-100 disabled:text-gray-800 disabled:cursor-not-allowed"
                              >
                                <option value="" disabled>
                                  所属を選択
                                </option>
                                {departmentOptions.map((dept) => (
                                  <option key={dept.id} value={dept.id}>
                                    {dept.name}
                                  </option>
                                ))}
                              </select>
                              {/* エラーメッセージ表示 */}
                              {errors["user.department_id"] && (
                                <p className="text-red-500 text-sm mt-1">
                                  <FontAwesomeIcon
                                    icon={faCircleExclamation}
                                    className="mr-1"
                                  />
                                  {errors["user.department_id"][0]}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>

                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            社員番号
                          </th>
                          <td className="px-4 py-2">
                            <div className="flex">
                              <input
                                value={user?.employee_id || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "employee_id",
                                    e.target.value
                                  )
                                }
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                            </div>
                            {/* エラーメッセージ表示 */}
                            {errors["user.employee_id"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.employee_id"][0]}
                              </p>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            IT企業入社年月日
                          </th>
                          <td className="px-4 py-2">
                            <div className="flex">
                              <input
                                type="date"
                                value={user?.it_company_join_date || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "it_company_join_date",
                                    e.target.value
                                  )
                                }
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                            </div>
                            {/* エラーメッセージ表示 */}
                            {errors["user.it_company_join_date"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.it_company_join_date"][0]}
                              </p>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            入社日
                          </th>
                          <td className="px-4 py-2">
                            <div className="flex">
                              <input
                                type="date"
                                value={user?.join_date || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange("join_date", e.target.value)
                                }
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                            </div>
                            {/* エラーメッセージ表示 */}
                            {errors["user.join_date"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.join_date"][0]}
                              </p>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            性別
                          </th>
                          <td className="px-4 py-2">
                            <select
                              defaultValue={user?.gender || ""}
                              disabled={
                                !(
                                  (
                                    isAdmin || // 管理者は常に編集可能
                                    isSystemAdmin || // システム管理者は常に編集可能
                                    (authUser?.id === user?.id &&
                                      !user?.is_saved_profile)
                                  ) // 自分自身でプロフィールが保存済みの場合
                                )
                              }
                              onChange={(e) =>
                                handleInputChange("gender", e.target.value)
                              }
                              className="border border-slate-300 px-2 py-2 rounded-lg focus:outline-none focus:ring focus:border-blue-300 w-full disabled:bg-gray-100 disabled:text-gray-800 disabled:cursor-not-allowed"
                            >
                              <option value="" disabled>
                                性別を選択
                              </option>
                              <option value="男性">男性</option>
                              <option value="女性">女性</option>
                              <option value="その他">その他</option>
                            </select>
                            {/* エラーメッセージ表示 */}
                            {errors["user.gender"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.gender"][0]}
                              </p>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            生年月日
                          </th>
                          <td className="px-4 py-2">
                            <div className="flex">
                              <input
                                type="date"
                                value={user?.birth_date || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "birth_date",
                                    e.target.value
                                  )
                                }
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                            </div>
                            {/* エラーメッセージ表示 */}
                            {errors["user.birth_date"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.birth_date"][0]}
                              </p>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            国籍
                          </th>
                          <td className=" px-4 py-2">
                            <div className="flex">
                              <input
                                type="text"
                                value={user?.nationality || ""}
                                disabled={
                                  !(
                                    (
                                      isAdmin || // 管理者は常に編集可能
                                      isSystemAdmin || // システム管理者は常に編集可能
                                      (authUser?.id === user?.id &&
                                        !user?.is_saved_profile)
                                    ) // 自分自身でプロフィールが保存済みの場合
                                  )
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "nationality",
                                    e.target.value
                                  )
                                }
                                placeholder="日本"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                            </div>
                            {/* エラーメッセージ表示 */}
                            {errors["user.nationality"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.nationality"][0]}
                              </p>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            郵便番号
                            <br />
                            <span className="font-normal text-xs">
                              (ハイフンなし7桁)
                            </span>
                          </th>
                          <td className="px-4 py-2">
                            <div className="flex">
                              <input
                                type="text"
                                inputMode="numeric"
                                maxLength={7}
                                value={user?.postal_code || ""}
                                placeholder="例: 1000001"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                onChange={(e) => {
                                  const zip = e.target.value.replace(
                                    /[^\d]/g,
                                    ""
                                  );
                                  handleInputChange("postal_code", zip);

                                  if (zip.length === 7) {
                                    fetchAddressByPostalCode(zip);
                                  }
                                }}
                              />
                            </div>
                            {errors["user.postal_code"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.postal_code"][0]}
                              </p>
                            )}
                          </td>
                        </tr>

                        <tr>
                          <th className="w-40 px-4 py-2 text-right border-r-2">
                            現住所
                          </th>
                          <td className="px-4 py-2">
                            <div className="flex">
                              <input
                                type="text"
                                value={user?.current_address || ""}
                                readOnly
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                              />
                            </div>
                            {errors["user.current_address"] && (
                              <p className="text-red-500 text-sm mt-1">
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="mr-1"
                                />
                                {errors["user.current_address"][0]}
                              </p>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* 保有資格テーブル */}
                  <div className="full">
                    <div className="flex mb-4">
                      <div className="rounded-full mt-1.5 border-slate-100 mr-3 w-5 h-5 title-circle"></div>
                      <div className="text-lg text-slate-500 font-black">
                        保有資格
                      </div>
                    </div>
                    <div className="w-full border-2 border-slate-300 rounded-2xl overflow-hidden">
                      <table className="table-auto text-slate-600 w-full border-collapse">
                        <thead className="border-b-2">
                          <tr>
                            <th className="bg-slate-100 px-4 py-2 border-r-2">
                              項番
                            </th>
                            <th className="bg-slate-100 px-4 py-2 border-r-2">
                              資格名称
                            </th>
                            <th className="bg-slate-100 px-4 py-2 border-r-2">
                              取得年月
                            </th>
                            <th className="bg-slate-100 px-4 py-2">削除</th>
                          </tr>
                        </thead>
                        <tbody>
                          {qualifications.length === 0 ? (
                            // 登録 0 件のときのメッセージ行
                            <tr>
                              <td
                                colSpan={4}
                                className="px-4 py-4 text-center text-slate-400 text-sm bg-white"
                              >
                                保有資格が登録されていません。
                              </td>
                            </tr>
                          ) : (
                            // 1件以上あるときは従来どおり表示
                            qualifications.map((qualification, idx) => (
                              <tr key={qualification.rowKey}>
                                <td
                                  className={`${
                                    idx % 2 === 0 ? "bg-slate-100" : "bg-eaf0f6"
                                  } px-4 py-2 text-center border-r-2`}
                                >
                                  {idx + 1}
                                </td>
                                <td
                                  className={`${
                                    idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                                  } px-4 py-2 border-r-2`}
                                >
                                  <select
                                    value={qualification.qualification_id || ""}
                                    onChange={(e) =>
                                      handleChangeQualification(
                                        idx,
                                        "qualification_id",
                                        e.target.value
                                      )
                                    }
                                    className="border border-slate-300 px-2 py-2 rounded-lg focus:outline-none focus:ring focus:border-blue-300"
                                  >
                                    <option value="" disabled>
                                      資格を選択
                                    </option>
                                    {qualificationOptions.map((option) => (
                                      <option key={option.id} value={option.id}>
                                        {option.name}
                                      </option>
                                    ))}
                                  </select>
                                  {/* エラーメッセージ表示 */}
                                  {errors[
                                    `qualifications.${idx}.qualification_id`
                                  ] && (
                                    <p className="text-red-500 text-sm mt-1">
                                      {
                                        errors[
                                          `qualifications.${idx}.qualification_id`
                                        ][0]
                                      }
                                    </p>
                                  )}
                                </td>
                                <td
                                  className={`${
                                    idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                                  } px-4 py-2 border-r-2`}
                                >
                                  <input
                                    type="month"
                                    value={
                                      qualification.get_date
                                        ? qualification.get_date.slice(0, 7)
                                        : ""
                                    }
                                    onChange={(e) =>
                                      handleChangeQualification(
                                        idx,
                                        "get_date",
                                        e.target.value
                                      )
                                    }
                                    className="border border-gray-300 rounded-lg p-2"
                                  />
                                  {/* エラーメッセージ表示 */}
                                  {errors[`qualifications.${idx}.get_date`] && (
                                    <p className="text-red-500 text-sm mt-1">
                                      {
                                        errors[
                                          `qualifications.${idx}.get_date`
                                        ][0]
                                      }
                                    </p>
                                  )}
                                </td>
                                <td
                                  className={`${
                                    idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                                  } px-4 py-2`}
                                >
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleQualificationDelete(qualification.rowKey!)
                                    }
                                    className="flex items-center justify-center w-6 h-6 bg-slate-600 text-white rounded-full hover:bg-slate-300"
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex mt-4">
                      <button
                        type="button"
                        onClick={handleQualificationAdd}
                        className="bg-gray-700 text-white py-1 px-4 mr-20 rounded-3xl hover:bg-opacity-50"
                      >
                        <FontAwesomeIcon icon={faCirclePlus} className="mr-2" />
                        資格を追加
                      </button>
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className="text-slate-400 mt-1"
                          style={{ fontSize: "1.25rem" }} // 高さ調整
                        />
                        <div className="flex flex-col ml-2 space-y-0.5">
                          <span className="text-slate-400 text-xs">
                            有効期限が失効した資格も削除せずに記載してください
                          </span>
                          <span className="text-slate-400 text-xs">
                            資格が選択肢にない場合は管理者に連絡してください
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
        {!isImageModalOpen && (
          <div className="fixed bottom-0 right-0 bg-gray-700 bg-opacity-30 text-white z-50 w-full h-16 flex items-center justify-end pr-10 space-x-5">
            <button
              onClick={handleSubmit}
              className="ignore-dirty bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
            >
              <FontAwesomeIcon icon={faCircleCheck} className="mr-2" />
              保存
            </button>
          </div>
        )}
      </div>
      {/* プロフィール画像を変更するモーダル */}
      <Modal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        title="プロフィール画像変更"
      >
        {/* ドラッグ＆ドロップorクリックエリア */}
        <div className="flex justify-center">
          <div
            className={`relative flex flex-col items-center justify-center p-4 w-96 h-96 border-2 rounded-lg cursor-pointer mb-1 transition-colors 
                      ${
                        isDragging
                          ? "border-blue-500 bg-blue-100"
                          : "border-gray-300 border-dashed"
                      }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isSaving ? (
              <div className="w-[340px] h-[340px] bg-slate-100 animate-pulse"></div>
            ) : previewUrl ? (
              <>
                <ReactCrop
                  crop={crop}
                  onChange={(c) => {
                    const newCrop = clampCrop(c);
                    setCrop(newCrop);
                  }}
                  keepSelection
                >
                  <img
                    id="preview"
                    ref={imageRef}
                    src={previewUrl}
                    alt="Crop preview"
                    className="w-[340px] h-[340px] object-contain"
                  />
                </ReactCrop>
                <button
                  onClick={handleDeletePreview}
                  className="absolute top-0 right-0 w-6 h-6 items-center justify-center text-white bg-black bg-opacity-50 rounded-full"
                >
                  ✖
                </button>
              </>
            ) : (
              <>
                <div className="w-[340px] h-[340px] bg-slate-100"></div>
                <input
                  type="file"
                  accept=".jpg, .jpeg, .png"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  onChange={handleChangeImage}
                />
                <p className="absolute text-gray-600 text-center text-sm">
                  ドラッグ＆ドロップ
                  <br />
                  または
                  <br />
                  クリックでファイルを選択
                  <br />
                  (jpg, jpeg, png形式、~1024KBまで)
                </p>
              </>
            )}
          </div>
        </div>
        {previewUrl && !isSaving ? (
          <p className="mt-2 text-center text-gray-600 text-sm">
            切り取り領域を調整してください
          </p>
        ) : (
          <p className="mt-2 text-center text-sm opacity-0 select-none">
            位置調整用透明文字
          </p>
        )}
        <div className="flex items-center justify-end">
          {isSaving ? (
            <div className="w-20 h-8 bg-slate-100 animate-pulse rounded-3xl"></div>
          ) : (
            <button
              onClick={handleSaveImage}
              className="ignore-dirty bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
            >
              <FontAwesomeIcon icon={faCircleCheck} className="mr-2" />
              保存
            </button>
          )}
        </div>
      </Modal>
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        title="保存確認"
        text={confirmText}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setIsConfirmModalOpen(false)}
      />
      <SuccessModal
        isOpen={isSuccessModalOpen}
        title={isNew ? "新規追加" : "保存成功"}
        text={
          isNew
            ? "ユーザーが新規追加されました。"
            : "ユーザー情報が保存されました。"
        }
        onClose={() => setSuccessModalOpen(false)}
      />
      <ErrorModal
        isOpen={isErrorModalOpen}
        title="エラー"
        text={errorMessage}
        onClose={() => setErrorModalOpen(false)}
      />
    </Layout>
  );
};

export default MyPage;
